import React, { useEffect, useState, useCallback } from "react";
import { Subheading } from "../../../../design/heading";
import { Button } from "../../../../design/button";
import { Text } from "../../../../design/text";
import { PlusIcon, ClipboardDocumentListIcon } from "@heroicons/react/20/solid";
import { useCaseManagement } from "../../../../contexts/CaseManagementContext";
import CaseManagementTimeline from "./CaseManagementTimeline";
import AddCaseManagementEventDialog from "./AddCaseManagementEventDialog";
import DeleteConfirmationDialog from "../../../../components/DeleteConfirmationDialog";
import {
  CaseManagementEvent,
  CreateCaseManagementEventRequest,
  UpdateCaseManagementEventRequest,
} from "@visitproof/types";

interface Props {
  caseId: string;
}

const CaseManagementTab: React.FC<Props> = ({ caseId }) => {
  const {
    events,
    loading,
    error,
    hasMore,
    fetchEvents,
    loadMore,
    createEvent,
    updateEvent,
    deleteEvent,
    clearEvents,
  } = useCaseManagement();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CaseManagementEvent | null>(null);
  const [deletingEvent, setDeletingEvent] = useState<CaseManagementEvent | null>(null);

  // Fetch events on mount
  useEffect(() => {
    fetchEvents(caseId);
    return () => {
      clearEvents();
    };
  }, [caseId, fetchEvents, clearEvents]);

  const handleOpenDialog = useCallback(() => {
    setEditingEvent(null);
    setIsDialogOpen(true);
  }, []);

  const handleEditEvent = useCallback((event: CaseManagementEvent) => {
    setEditingEvent(event);
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setEditingEvent(null);
  }, []);

  const handleSaveEvent = useCallback(
    async (
      data: CreateCaseManagementEventRequest | Omit<UpdateCaseManagementEventRequest, "eventId">
    ) => {
      if (editingEvent) {
        await updateEvent(editingEvent.id, data as Omit<UpdateCaseManagementEventRequest, "eventId">);
      } else {
        await createEvent(data as CreateCaseManagementEventRequest);
      }
    },
    [editingEvent, createEvent, updateEvent]
  );

  const handleDeleteEvent = useCallback((event: CaseManagementEvent) => {
    setDeletingEvent(event);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deletingEvent) {
      await deleteEvent(deletingEvent.id);
      setDeletingEvent(null);
    }
  }, [deletingEvent, deleteEvent]);

  const handleLoadMore = useCallback(() => {
    loadMore(caseId);
  }, [caseId, loadMore]);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between w-full gap-4">
        <div>
          <Subheading>Case Management</Subheading>
          <Text className="text-sm text-zinc-500 dark:text-zinc-400">
            Track communications, interventions, and case-related events
          </Text>
        </div>
        <Button onClick={handleOpenDialog}>
          <PlusIcon className="w-4 h-4" />
          Add Event
        </Button>
      </div>

      {/* Error state */}
      {error.list && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <Text className="text-sm text-red-600 dark:text-red-400">
            Failed to load events. Please try again.
          </Text>
        </div>
      )}

      {/* Empty state */}
      {!loading.list && events.length === 0 && !error.list && (
        <div className="mt-8 flex flex-col items-center justify-center py-12 text-center">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-zinc-100 dark:bg-zinc-800 mb-4">
            <ClipboardDocumentListIcon className="w-8 h-8 text-zinc-400" />
          </div>
          <Text className="text-lg font-medium text-zinc-700 dark:text-zinc-300">
            No case management events yet
          </Text>
          <Text className="mt-1 text-sm text-zinc-500 dark:text-zinc-400 max-w-sm">
            Track client communications, interventions, referrals, and court updates to maintain a
            comprehensive case history.
          </Text>
          <Button className="mt-4" onClick={handleOpenDialog}>
            <PlusIcon className="w-4 h-4" />
            Add First Event
          </Button>
        </div>
      )}

      {/* Loading state */}
      {loading.list && events.length === 0 && (
        <div className="mt-8 flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-300 border-t-indigo-600" />
          <Text className="ml-3 text-sm text-zinc-500 dark:text-zinc-400">
            Loading events...
          </Text>
        </div>
      )}

      {/* Timeline */}
      {events.length > 0 && (
        <div className="mt-6">
          <CaseManagementTimeline
            events={events}
            hasMore={hasMore}
            loading={loading.list || false}
            onLoadMore={handleLoadMore}
            onEdit={handleEditEvent}
            onDelete={handleDeleteEvent}
          />
        </div>
      )}

      {/* Add/Edit Dialog */}
      <AddCaseManagementEventDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveEvent}
        caseId={caseId}
        event={editingEvent}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={!!deletingEvent}
        onClose={() => setDeletingEvent(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Event"
        message="Are you sure you want to delete this case management event? This action cannot be undone."
        confirmText="Delete Event"
      />
    </>
  );
};

export default CaseManagementTab;
